/**
 * KAN-756: ячейки таблицы доски CRM. Пустое значение: прочерк "-".
 * KAN-757: контакт и Telegram правятся на месте (CrmFieldEditors); столбец "Дата".
 * KAN-759: столбец "Этап" (этап бейджем, исход "Успех"/"Отказ"); заказы
 * открываются модалками (кнопки вместо ссылок).
 * KAN-780: "Текущий заказ" показывает только черновик или прочерк; создание и
 * открытие заказа - кнопка-иконка OrderActionButton в ячейке действий (стиль
 * compact-кнопок AttentionActionButtons).
 * KAN-790: "Причина" (ReasonCell) под заголовком причины показывает задачу
 * менеджеру: текст, срок по времени организации (KAN-840) и пометку "просрочено" (форматтер блока внимания).
 * KAN-880: ячейки таблицы лидов: группа (колонка доски) и дни на этапе / в работе.
 * KAN-958: CrmOrdersCell - текущий заказ и прошлые одним столбцом таблицы; OrdersCell и
 * CurrentOrderCell остаются для панели карточки доски.
 * KAN-957: lines у TextCell и clamp у ReasonCell: длинный текст не растягивает строку
 * таблицы (line-clamp, полный текст в title); без них текст целиком (панель карточки).
 */
import { ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { attentionTask, formatTaskLine } from '@/components/dashboard/attentionFormat';
import {
  EMPTY_CELL,
  formatCrmDayTitle,
  formatCrmMoney,
  formatNextContact,
  formatShortDateTime,
  isNextContactDue,
} from '@/lib/crmBoard';
import { leadDaysText } from '@/lib/crmLeads';
import type { CrmTableRow } from '@/lib/crmTable';
import type { CrmCurrentOrder, CrmLeadRow, CrmOrder, CrmOutcome, CrmRow, CrmStage } from '@/types/crm';

const LINK = 'text-left text-blue-600 hover:underline disabled:opacity-50';
const BADGE = 'whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium';

const OUTCOME_VIEW: Partial<Record<CrmOutcome, { label: string; className: string }>> = {
  won: { label: 'Успех', className: 'bg-green-100 text-green-800' },
  lost: { label: 'Отказ', className: 'bg-red-100 text-red-700' },
};

/** KAN-957: сколько строк текста видно в ячейке таблицы. */
export type CellLines = 2 | 3;
const CLAMP: Record<CellLines, string> = { 2: 'line-clamp-2', 3: 'line-clamp-3' };

/** KAN-957: текст не выше lines строк, остальное троеточием; полный текст в подсказке. */
export function ClampedText({ text, lines }: { text: string; lines: CellLines }) {
  return (
    <span className={`${CLAMP[lines]} whitespace-pre-wrap break-words`} title={text}>
      {text}
    </span>
  );
}

/** lines: в таблице не выше стольких строк (KAN-957); без него текст целиком. */
export function TextCell({ value, lines }: { value: string | null | undefined; lines?: CellLines }) {
  if (!value?.trim()) return <>{EMPTY_CELL}</>;
  return lines ? <ClampedText text={value} lines={lines} /> : <>{value}</>;
}

/** KAN-790: причина внимания и задача менеджеру под ней (без поля task: только причина). */
export function ReasonCell({
  row,
  timeZone,
  clamp = false,
}: {
  row: Pick<CrmRow, 'reason' | 'task'>;
  timeZone?: string;
  /** KAN-957: в таблице причина не выше 3 строк; задача и бейдж под ней целиком. */
  clamp?: boolean;
}) {
  const task = attentionTask(row);
  const lines = clamp ? 3 : undefined;
  if (!task) return <TextCell value={row.reason} lines={lines} />;
  const reason = row.reason?.trim() ? row.reason : null;
  return (
    <>
      {reason && <div>{lines ? <ClampedText text={reason} lines={lines} /> : reason}</div>}
      <div data-testid="crm-reason-task" className="text-xs text-gray-600">
        {formatTaskLine(task, timeZone)}
        {task.overdue && (
          <span data-testid="crm-reason-task-overdue" className={`ml-1 bg-red-100 text-red-700 ${BADGE}`}>
            просрочено
          </span>
        )}
      </div>
    </>
  );
}

/** KAN-840: `timeZone` - пояс организации (по умолчанию Москва). */
export function NextContactCell({ value, timeZone }: { value: string | null; timeZone?: string }) {
  const text = formatNextContact(value, timeZone);
  if (!text) return <>{EMPTY_CELL}</>;
  const due = isNextContactDue(value, undefined, timeZone);
  return (
    <span
      data-testid="crm-next-contact"
      data-due={due ? 'true' : 'false'}
      className={due ? 'rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800' : ''}
    >
      {text}
    </span>
  );
}

/** Открыть заказ; не передан: заказ показывается текстом (KAN-811: заказы закрыты роли MANAGER). */
type OpenOrder = ((orderId: string) => void) | undefined;

function OrderButton({ order, onOpen }: { order: CrmOrder | CrmCurrentOrder; onOpen: OpenOrder }) {
  const money = formatCrmMoney(order.total, order.currency);
  const content = (
    <>
      {order.title || 'Заказ'}
      {money && <span className="ml-1 text-gray-500">{money}</span>}
    </>
  );
  if (!onOpen) return <span data-testid="crm-order-text">{content}</span>;
  return (
    <button type="button" className={LINK} onClick={() => onOpen(order.id)}>
      {content}
    </button>
  );
}

export function OrdersCell({ orders, onOpen }: { orders: CrmOrder[]; onOpen: OpenOrder }) {
  if (orders.length === 0) return <>{EMPTY_CELL}</>;
  return (
    <ul className="space-y-0.5">
      {orders.map((order) => (
        <li key={order.id}>
          <OrderButton order={order} onOpen={onOpen} />
        </li>
      ))}
    </ul>
  );
}

interface CrmOrdersCellProps {
  current: CrmCurrentOrder | null;
  orders: CrmOrder[];
  /** Черновик открывается оформлением заказа. */
  onOpenCurrent: OpenOrder;
  /** Прошлый заказ открывается просмотром. */
  onOpenPast: OpenOrder;
}

/**
 * KAN-958: столбец "Заказы" таблицы CRM: сначала текущий заказ (черновик) с
 * пометкой "текущий", затем прошлые заказы; пусто - прочерк.
 */
export function CrmOrdersCell({ current, orders, onOpenCurrent, onOpenPast }: CrmOrdersCellProps) {
  const past = current ? orders.filter((order) => order.id !== current.id) : orders;
  if (!current && past.length === 0) return <>{EMPTY_CELL}</>;
  return (
    <ul className="space-y-0.5">
      {current && (
        <li>
          <OrderButton order={current} onOpen={onOpenCurrent} />
          <span data-testid="crm-current-order-mark" className={`${BADGE} ml-1 bg-blue-50 text-blue-700`}>
            текущий
          </span>
        </li>
      )}
      {past.map((order) => (
        <li key={order.id}>
          <OrderButton order={order} onOpen={onOpenPast} />
        </li>
      ))}
    </ul>
  );
}

interface CurrentOrderCellProps {
  order: CrmCurrentOrder | null;
  onOpen: OpenOrder;
}

/** Текущий черновик клиента кнопкой; без черновика: прочерк (KAN-780). */
export function CurrentOrderCell({ order, onOpen }: CurrentOrderCellProps) {
  if (!order) return <>{EMPTY_CELL}</>;
  return <OrderButton order={order} onOpen={onOpen} />;
}

interface OrderActionButtonProps {
  order: CrmCurrentOrder | null;
  /** Черновик для этого клиента сейчас создаётся. */
  creating?: boolean;
  onOpen: (orderId: string) => void;
  onCreate: () => void;
}

/**
 * KAN-780: кнопка-иконка заказа в ячейке действий. Без черновика создаёт
 * новый заказ ("Новый заказ"), с черновиком открывает его ("Открыть заказ").
 */
export function OrderActionButton({ order, creating = false, onOpen, onCreate }: OrderActionButtonProps) {
  const label = order ? 'Открыть заказ' : 'Новый заказ';
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-7 w-7 p-0"
      data-testid="crm-order-action"
      title={label}
      aria-label={label}
      disabled={!order && creating}
      onClick={() => (order ? onOpen(order.id) : onCreate())}
    >
      <ShoppingCart className="h-3.5 w-3.5" />
    </Button>
  );
}

/** Этап воронки бейджем; без этапа: прочерк. */
export function CrmStageBadge({ stage }: { stage?: CrmStage | null }) {
  if (!stage) return <>{EMPTY_CELL}</>;
  return (
    <span data-testid="crm-stage" className={`${BADGE} bg-gray-100 text-gray-700`}>
      {stage.name}
    </span>
  );
}

/** Исход на этапе бейджем ("Успех"/"Отказ"); в работе: ничего. */
export function CrmOutcomeBadge({ outcome }: { outcome?: CrmOutcome }) {
  const result = outcome ? OUTCOME_VIEW[outcome] : undefined;
  if (!result || !outcome) return null;
  return (
    <span data-testid="crm-outcome" data-outcome={outcome} className={`${BADGE} ${result.className}`}>
      {result.label}
    </span>
  );
}

/** Этап воронки бейджем и исход на этапе (только просмотр). */
export function CrmStageCell({ stage, outcome }: { stage?: CrmStage | null; outcome?: CrmOutcome }) {
  const result = outcome ? OUTCOME_VIEW[outcome] : undefined;
  if (!stage && !result) return <>{EMPTY_CELL}</>;
  return (
    <div className="flex flex-wrap gap-1">
      {stage && <CrmStageBadge stage={stage} />}
      <CrmOutcomeBadge outcome={outcome} />
    </div>
  );
}

/** KAN-880: группа лида (колонка доски) и отметка внимания (таблица лидов). */
export function CrmLeadColumnCell({ row, labels }: { row: CrmRow; labels: Record<string, string> }) {
  const lead = row as Partial<CrmLeadRow>;
  const label = lead.column ? (labels[lead.column] ?? lead.column) : EMPTY_CELL;
  return (
    <div className="space-y-1">
      <span data-testid="crm-cell-column" className="block font-medium text-gray-800">
        {label}
      </span>
      {lead.attention && (
        <span
          data-testid="crm-attention-badge"
          className="inline-block whitespace-nowrap rounded bg-rose-100 px-1.5 py-0.5 text-xs font-medium text-rose-700"
        >
          Требует внимания
        </span>
      )}
    </div>
  );
}

/** KAN-880: дни лида: "N дней в работе" у взятых, иначе "N дней на этапе". */
export function CrmLeadDaysCell({ row }: { row: CrmRow }) {
  return (
    <span data-testid="crm-cell-days" className="text-gray-700">
      {leadDaysText(row) ?? EMPTY_CELL}
    </span>
  );
}

/** KAN-883: дата и время взятия в работу (taken_at по Москве), нет - прочерк. */
export function CrmTakenCell({ row }: { row: CrmRow }) {
  const text = formatShortDateTime(row.taken_at);
  return (
    <span data-testid="crm-cell-taken" className="text-gray-700">
      {text ?? EMPTY_CELL}
    </span>
  );
}

/** Дата строки: отметка внимания или дата отработки по Москве. */
export function CrmDateCell({ item }: { item: CrmTableRow }) {
  if (item.kind === 'attention') {
    return (
      <span
        data-testid="crm-attention-badge"
        className="whitespace-nowrap rounded bg-rose-100 px-1.5 py-0.5 text-xs font-medium text-rose-700"
      >
        Сегодня · требует внимания
      </span>
    );
  }
  const text = formatNextContact(item.row.done_at) ?? (item.day ? formatCrmDayTitle(item.day) : null);
  return <span data-testid="crm-done-date">{text ?? EMPTY_CELL}</span>;
}
